// =====================================================================
// My Agency V2 — overlays (ARTBOARD-SPEC §4 CMP-MODAL, CMP-CONFIRM,
// CMP-POPOVER, CMP-SHEET). Purely presentational; open/close state and
// all actions are wired by the caller (existing handlers only).
//
// Portal idiom matches the existing ChangeAgentModal
// (createPortal(document.body), z-[100]).
// =====================================================================
import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { CloseIcon } from "./icons";
import { SheetGrabber } from "./glyphs";
import { IconButton, Button } from "./buttons";

// ── CMP-MODAL — desktop modal ────────────────────────────────────────
// Scrim rgba(15,23,42,.45); panel 400–520px r14, shadow, padding 22–24.
// footnote (optional) switches the footer to space-between.
export const Modal = ({
  open,
  onClose,
  title,
  subtitle,
  width = 460,
  padding = 24,
  footer,
  footnote,
  closeLabel,
  hideClose = false,
  children,
}) => {
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center font-tpv2"
      style={{ background: "rgba(15,23,42,.45)", padding: 32 }}
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-h-full overflow-y-auto"
        style={{
          maxWidth: width,
          borderRadius: 14,
          boxShadow: "0 24px 64px rgba(15,23,42,.3)",
          padding,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {(title || !hideClose) && (
          <div
            className="flex justify-between gap-3"
            style={{ alignItems: subtitle ? "flex-start" : "center" }}
          >
            <div className="min-w-0">
              {title && (
                <div className="text-[17px] font-semibold text-[#0F172A]">{title}</div>
              )}
              {subtitle && (
                <div className="text-[13px] text-[#64748B] mt-[3px]">{subtitle}</div>
              )}
            </div>
            {!hideClose && (
              <IconButton onClick={onClose} aria-label={closeLabel} title={closeLabel}>
                <CloseIcon size={15} strokeWidth={2.2} />
              </IconButton>
            )}
          </div>
        )}
        {children}
        {footer && (
          <div
            className="flex items-center gap-2.5"
            style={{
              marginTop: 20,
              justifyContent: footnote ? "space-between" : "flex-end",
            }}
          >
            {footnote && <span className="text-xs text-[#94A3B8]">{footnote}</span>}
            <div className="flex items-center gap-2.5">{footer}</div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

// ── CMP-CONFIRM — confirmation dialog ────────────────────────────────
// tone: "danger" (red icon circle) | "affirmative" (brand icon circle).
// icon: the 18px symbol node (SYM-12/22/23/06/21 per board).
// mobile: r16 panel, stacked full-width buttons (action first).
export const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  tone = "danger",
  icon,
  title,
  body,
  confirmLabel,
  cancelLabel,
  loading = false,
  mobile = false,
  width = 420,
}) => {
  if (!open) return null;
  const iconBg = tone === "danger" ? "#FEF2F2" : "#EAF3FF";
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center font-tpv2"
      style={{ background: "rgba(15,23,42,.45)", padding: mobile ? 24 : 32 }}
      onClick={loading ? undefined : onClose}
    >
      <div
        className="bg-white w-full"
        style={{
          maxWidth: width,
          borderRadius: mobile ? 16 : 14,
          boxShadow: "0 24px 64px rgba(15,23,42,.3)",
          padding: 22,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </div>
        <div
          className="font-semibold text-[#0F172A]"
          style={{ marginTop: 14, fontSize: mobile ? 16.5 : 16, lineHeight: mobile ? 1.3 : undefined }}
        >
          {title}
        </div>
        <div className="text-[13.5px] text-[#475569]" style={{ marginTop: 8, lineHeight: 1.55 }}>
          {body}
        </div>
        {mobile ? (
          <div className="flex flex-col" style={{ marginTop: 20, gap: 9 }}>
            <Button
              mobile
              variant={tone === "danger" ? "danger" : "primary"}
              onClick={onConfirm}
              disabled={loading}
            >
              {confirmLabel}
            </Button>
            <Button mobile variant="sheetCancel" onClick={onClose} disabled={loading}>
              {cancelLabel}
            </Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2.5" style={{ marginTop: 20 }}>
            <Button variant="secondary" onClick={onClose} disabled={loading}>
              {cancelLabel}
            </Button>
            <Button
              variant={tone === "danger" ? "danger" : "primary"}
              onClick={onConfirm}
              disabled={loading}
            >
              {confirmLabel}
            </Button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

// ── CMP-POPOVER — overflow menu ──────────────────────────────────────
// Anchored by the PARENT (position:absolute props via style/className).
// Outside-click close is presentation chrome (same idiom as the old
// AgentCardPopover); business actions stay caller-side.
export const Popover = ({ onClose, width = 216, className = "", style, children, ...rest }) => {
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose && onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);
  return (
    <div
      ref={ref}
      className={`bg-white font-tpv2 ${className}`}
      style={{
        width,
        border: "1px solid #E4E9F0",
        borderRadius: 11,
        boxShadow: "0 12px 32px rgba(15,23,42,.16)",
        padding: 6,
        zIndex: 5,
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
};

// Popover menu item. icon = 14px symbol node. `subtitle` renders the
// consequence sub-line (e.g. under "Make admin").
export const PopoverItem = ({
  icon,
  subtitle,
  destructive = false,
  disabled = false,
  className = "",
  style,
  children,
  ...rest
}) => (
  <div
    role="button"
    aria-disabled={disabled || undefined}
    className={[
      "flex items-center gap-[9px] rounded-[7px]",
      disabled
        ? "text-[#94A3B8] cursor-not-allowed"
        : destructive
        ? "text-[#DC2626] font-medium cursor-pointer hover:bg-[#FEF2F2]"
        : "text-[#334155] cursor-pointer hover:bg-[#F5F8FC]",
      className,
    ].join(" ")}
    style={{ padding: "8px 10px", fontSize: 13.5, ...style }}
    {...rest}
  >
    {icon}
    <span className="min-w-0 flex-1">
      <span className="block truncate">{children}</span>
      {subtitle && (
        <span className="block text-[11.5px] text-[#94A3B8] leading-[1.45]">{subtitle}</span>
      )}
    </span>
  </div>
);

// ── CMP-SHEET — mobile bottom sheet ──────────────────────────────────
// Scrim rgba(15,23,42,.5); sheet pinned bottom, r 22 22 0 0. `top` sets
// the tall-sheet offset; omit for auto-height action sheets. Footer slot
// pins via margin-top:auto.
export const Sheet = ({
  open,
  onClose,
  title,
  top,
  padding = "12px 20px 36px",
  closeLabel,
  hideClose = false,
  grabberMargin = 14,
  footer,
  children,
}) => {
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[100] font-tpv2"
      style={{ background: "rgba(15,23,42,.5)" }}
      onClick={onClose}
    >
      <div
        className="absolute left-0 right-0 bottom-0 bg-white flex flex-col overflow-hidden"
        style={{
          top: top ?? "auto",
          borderRadius: "22px 22px 0 0",
          boxShadow: "0 -12px 40px rgba(15,23,42,.25)",
          padding,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <SheetGrabber marginBottom={grabberMargin} />
        {(title || !hideClose) && (
          <div className="flex items-center justify-between gap-3">
            {title && <div className="text-[17px] font-semibold text-[#0F172A]">{title}</div>}
            {!hideClose && (
              <IconButton
                variant="mobileClose"
                onClick={onClose}
                aria-label={closeLabel}
                title={closeLabel}
              >
                <CloseIcon size={14} strokeWidth={2.2} />
              </IconButton>
            )}
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto flex flex-col">{children}</div>
        {footer && (
          <div className="flex flex-col gap-[9px]" style={{ marginTop: "auto", paddingTop: 12 }}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};
